import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Lock, KeyRound, ShieldCheck, FileCheck, Check } from "lucide-react";
import { Reveal, MaskedLines, Marquee, SectionHead, EASE } from "@/lib/motion";
import Countdown from "@/components/Countdown";
import HeroPerks from "@/components/HeroPerks";
import { TRUST_ITEMS, SERVICES, PATHS } from "@/data/content";
import jaredFamily from "@/assets/jared-family.jpg";

const HERO_IMG = "https://images.pexels.com/photos/10426651/pexels-photo-10426651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const VAULT_IMG = "https://images.unsplash.com/photo-1582139329536-e7284fece509";

const PHILOSOPHY_GOALS = [
  "Review the information you provide.",
  "Ask questions when something doesn't make sense.",
  "Look for deductions and credits that legitimately apply to your situation.",
  "Explain significant items on your return.",
  "Give you an opportunity to review your return before filing.",
  "Help you understand what you owe, what you're receiving, and why.",
];

const TRANSPARENCY = [
  "We don't promise a particular refund before reviewing your information.",
  "We don't invent deductions.",
  "We don't base tax advice on \u201cwhat someone got away with last year.\u201d",
  "We ask questions when information doesn't make sense.",
  "You review your return before it is filed.",
  "Your refund belongs to you.",
];

const SECURITY_POINTS = [
  { icon: Lock, title: "Secure document portal", desc: "Sensitive tax documents move through a secure client portal — never ordinary email." },
  { icon: KeyRound, title: "Multi-factor authentication", desc: "Accounts containing sensitive information are protected with MFA where available." },
  { icon: ShieldCheck, title: "Controlled access", desc: "Client information is accessible only when necessary to provide services." },
  { icon: FileCheck, title: "Written security procedures", desc: "Steele Industries maintains written procedures designed to protect taxpayer information." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const [path, setPath] = useState(PATHS[0]);
  const [aboutHover, setAboutHover] = useState(false);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt=""
            className="h-[120%] w-full object-cover opacity-25 grayscale contrast-125"
          />
        </motion.div>
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div
            className="aurora"
            style={{ width: "46rem", height: "46rem", right: "-10rem", top: "-12rem", background: "radial-gradient(circle, rgba(168,85,247,0.22), transparent 65%)", animation: "aurora-a 16s ease-in-out infinite" }}
          />
          <div
            className="aurora"
            style={{ width: "38rem", height: "38rem", left: "-12rem", bottom: "-8rem", background: "radial-gradient(circle, rgba(217,70,239,0.12), transparent 65%)", animation: "aurora-b 20s ease-in-out infinite" }}
          />
          <div
            className="absolute inset-0 bg-grid"
            style={{ maskImage: "linear-gradient(to bottom, black 20%, transparent 90%)", WebkitMaskImage: "linear-gradient(to bottom, black 20%, transparent 90%)" }}
          />
          <div className="absolute inset-x-0 bottom-0 h-48" style={{ background: "linear-gradient(to top, #07070A, transparent)" }} />
        </div>
        <div className="scanline" />

        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 pt-32 pb-24 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-steel-brass"
          >
            Steele Industries — Tax Preparation & Accounting Services
          </motion.p>
          <h1 className="font-display text-5xl font-medium leading-[1.04] tracking-tighter sm:text-7xl lg:text-[6.2rem]" data-testid="hero-headline">
            <MaskedLines
              lines={[
                <>Taxes, handled —</>,
                <>and <span className="text-steel-brass neon-text">explained clearly.</span></>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-steel-secondary sm:text-lg"
          >
            Professional tax preparation backed by experience assisting in the preparation of more than
            1,300 individual tax returns. Clear communication, secure document handling, and a process
            designed to keep you informed from start to finish.
          </motion.p>
          </div>
          <div className="lg:col-span-4 space-y-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
            >
              <Link
                to="/about"
                data-testid="hero-about-card"
                aria-label="About Jared Steele"
                onMouseEnter={() => setAboutHover(true)}
                onMouseLeave={() => setAboutHover(false)}
                onFocus={() => setAboutHover(true)}
                onBlur={() => setAboutHover(false)}
                className="group flex items-center gap-4 border border-steel-border bg-steel-surface/70 p-4 backdrop-blur-md transition-colors duration-300 hover:border-steel-brass/50 hover:bg-steel-surface"
              >
                <img
                  src={jaredFamily}
                  alt="Jared Steele"
                  data-testid="hero-about-photo"
                  className="h-14 w-14 shrink-0 rounded-full object-cover grayscale transition-all duration-300 group-hover:grayscale-0"
                  style={{ objectPosition: "center 25%" }}
                />
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-brass">
                    Who prepares your return
                  </p>
                  <p className="mt-1 font-display text-base font-medium leading-tight text-steel-text">
                    Jared Steele
                  </p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-steel-secondary">
                    Founder &amp; Tax Preparer — meet Jared
                    <ArrowUpRight className="h-3 w-3 shrink-0 text-steel-muted transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-steel-brass" />
                  </p>
                  <motion.div
                    initial={false}
                    animate={{ height: aboutHover ? "auto" : 0, opacity: aboutHover ? 1 : 0, marginTop: aboutHover ? 8 : 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <p
                      data-testid="hero-about-credentials"
                      className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-steel-brass"
                    >
                      <span>PTIN</span>
                      <span className="text-steel-muted">·</span>
                      <span>ERO</span>
                      <span className="text-steel-muted">·</span>
                      <span>EFIN</span>
                    </p>
                  </motion.div>
                </div>
              </Link>
            </motion.div>
            <HeroPerks />
            <Countdown />
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <div className="border-y border-dashed border-steel-border bg-steel-surface/60 py-6 backdrop-blur-md">
        <Marquee items={TRUST_ITEMS} />
      </div>

      {/* 4-PATH SELECTOR */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="path-selector-section">
        <SectionHead
          overline="Start here"
          title="What kind of tax help do you need?"
          copy="Four doors, one standard of care. Pick the one that sounds like you."
        />
        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PATHS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <button
                data-testid={`path-option-${p.id}`}
                onClick={() => setPath(p)}
                className={`group flex h-full w-full flex-col justify-between border p-7 text-left transition-colors duration-300 ${
                  path.id === p.id
                    ? "border-steel-brass bg-steel-surface shadow-[0_0_28px_rgba(168,85,247,0.14)]"
                    : "border-steel-border bg-transparent hover:border-steel-muted hover:bg-steel-surface/60"
                }`}
              >
                <span className="font-mono text-xs text-steel-muted">0{i + 1}</span>
                <span className="mt-10 flex items-end justify-between gap-2">
                  <span className="font-display text-xl font-medium leading-tight text-steel-text">{p.label}</span>
                  <ArrowUpRight
                    className={`h-5 w-5 shrink-0 transition-all duration-300 ${
                      path.id === p.id ? "text-steel-brass" : "text-steel-muted group-hover:text-steel-text"
                    }`}
                  />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={path.id}
            data-testid="path-detail-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="mt-6 grid grid-cols-1 gap-10 border border-steel-border glass p-10 lg:grid-cols-12 lg:p-14"
          >
            <div className="lg:col-span-7">
              <h3 className="font-display text-2xl font-medium text-steel-text sm:text-3xl">{path.title}</h3>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-steel-secondary">{path.desc}</p>
              <Link
                to={path.cta.href}
                data-testid="path-cta-btn"
                className="group mt-8 inline-flex items-center gap-2 border-b border-steel-brass pb-1 text-sm font-semibold text-steel-brass transition-colors hover:text-steel-text"
              >
                {path.cta.label}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
            <ul className="space-y-4 lg:col-span-5">
              {path.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-steel-secondary">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-steel-brass" />
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* BLACK BOX PHILOSOPHY */}
      <section className="border-t border-steel-border" data-testid="philosophy-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-12 lg:px-10 lg:py-36">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <SectionHead
                overline="Our philosophy"
                title="Tax preparation shouldn't feel like a black box."
                copy="You should know what is happening with your return. You shouldn't simply drop off documents, receive a number, and have no idea how it was calculated."
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-muted">
                At Steele Industries, the goal is to:
              </p>
            </Reveal>
            <div className="mt-8">
              {PHILOSOPHY_GOALS.map((g, i) => (
                <Reveal key={g} delay={i * 0.06}>
                  <div className="flex items-baseline gap-6 border-b border-dashed border-steel-border py-6">
                    <span className="font-mono text-sm text-steel-brass">0{i + 1}</span>
                    <p className="font-display text-xl font-medium leading-snug text-steel-text sm:text-2xl">{g}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-8 text-sm leading-relaxed text-steel-muted">
                This lines up with what the IRS itself tells taxpayers to look for: reputable preparers
                request records, ask appropriate questions, and give you the opportunity to review your
                return before signing.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="border-t border-steel-border" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
          <SectionHead
            overline="What can we help with?"
            title="Find yourself in the list."
            copy="Not “Tax Preparation” as a vague promise — the actual situations we handle, in plain terms."
          />
          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12">
            {SERVICES.map((s, i) => {
              const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-5", "md:col-span-7", "md:col-span-6", "md:col-span-6"];
              return (
                <Reveal key={s.id} delay={(i % 3) * 0.08} className={spans[i]}>
                  <Link
                    to={`/tax-services#${s.id}`}
                    data-testid={`service-card-${s.id}`}
                    className="group flex h-full flex-col justify-between border border-steel-border bg-steel-surface p-8 transition-all duration-300 hover:-translate-y-1 hover:border-steel-brass/50"
                  >
                    <div>
                      <span className="font-mono text-xs text-steel-muted">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="mt-4 font-display text-2xl font-medium text-steel-text">{s.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-steel-secondary">{s.desc}</p>
                    </div>
                    <span className="mt-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-steel-muted transition-colors duration-300 group-hover:text-steel-brass">
                      Learn more <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="border-t border-steel-border" data-testid="security-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-12 lg:px-10 lg:py-36">
          <div className="lg:col-span-6">
            <SectionHead
              overline="Data security"
              title="Your financial information deserves serious protection."
              copy="Not vague claims — what we actually do."
            />
            <div className="mt-12 space-y-8">
              {SECURITY_POINTS.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.07}>
                  <div className="flex gap-5">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
                      <p.icon className="h-5 w-5 text-steel-brass" />
                    </div>
                    <div>
                      <h4 className="font-display text-lg font-medium text-steel-text">{p.title}</h4>
                      <p className="mt-1.5 text-sm leading-relaxed text-steel-secondary">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1} className="h-full">
              <div className="relative h-full min-h-[420px] overflow-hidden border border-steel-border">
                <img src={VAULT_IMG} alt="Steel vault" className="absolute inset-0 h-full w-full object-cover grayscale contrast-125" />
                <div className="absolute inset-0 bg-steel-bg/45" />
                <div className="absolute bottom-0 left-0 p-8">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-brass">WISP</p>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-steel-text">
                    Tax professionals are required to maintain a Written Information Security Plan. We treat
                    that as a floor, not a ceiling.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="border-t border-steel-border bg-steel-surface/40" data-testid="transparency-section">
        <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
          <SectionHead
            overline="Straight dealing"
            title="No mystery. No refund games."
          />
          <div className="mt-14 grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
            {TRANSPARENCY.map((t, i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="flex items-start gap-4 border-b border-dotted border-steel-border pb-6">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-steel-brass" />
                  <p className="text-base leading-relaxed text-steel-secondary">{t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-3xl text-sm leading-relaxed text-steel-muted">
              The IRS explicitly warns taxpayers about preparers who promise unusually large refunds, charge
              based on refund size, refuse to sign returns, or direct refunds into their own accounts. None
              of that happens here.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMPLEXITY CTA */}
      <section className="border-t border-steel-border" data-testid="complexity-cta-section">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-6 py-24 lg:flex-row lg:items-center lg:px-10">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-medium leading-tight text-steel-text sm:text-4xl">
              Not sure how complex your return is?
            </h2>
            <p className="mt-4 max-w-xl text-base text-steel-secondary">
              Sixty seconds, fourteen checkboxes, and an honest read on what your return involves — before
              you commit to anything.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/complexity"
              data-testid="complexity-check-btn"
              className="group flex items-center gap-2 border border-steel-border px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-surface"
            >
              Run the Complexity Check
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-steel-border bg-mesh" data-testid="final-cta-section">
        <div className="mx-auto max-w-7xl px-6 py-28 text-left lg:px-10 lg:py-36">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-steel-brass">Ready when you are</p>
            <h2 className="mt-6 max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-steel-text sm:text-6xl">
              Put your return in experienced hands.
            </h2>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/get-started"
                data-testid="final-start-return-btn"
                className="group flex items-center justify-center gap-2 bg-steel-accent px-8 py-4 text-sm font-semibold tracking-wide text-white transition-colors duration-300 hover:bg-steel-accentHover"
              >
                Start My Tax Return
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/portal?signup=1"
                data-testid="final-create-account-btn"
                className="glass flex items-center justify-center gap-2 border border-steel-brass/50 px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-elevated"
              >
                Create My Portal Account
              </Link>
              <Link
                to="/how-it-works"
                data-testid="final-how-it-works-btn"
                className="flex items-center justify-center gap-2 border border-steel-border px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-bg"
              >
                See How It Works
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
